import clsx from 'clsx';
import { forwardRef, type ReactNode, type ReactElement } from 'react';
import { isElement } from 'react-is';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';

import { visualContainerCSS } from './Visual.sty.js';
import { visualSlotCss } from '../EmptyState.sty.js';

//#region Typings
/**
 * @public
 */
export interface EmptyStateVisualProps
  extends WithChildren,
    StylingProps,
    DataTestId {}
//#endregion Typings

/**
 * Typeguard function that will check if a ReactNode is a EmptyState.Visual
 * @internal
 */
export function isEmptyStateVisual(
  child: ReactNode,
): child is ReactElement<EmptyStateVisualProps> {
  return isElement(child) && child.type === Visual;
}

/**
 * Container for the Visual slot of the EmptyState component.
 * @public
 */
export const Visual = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  EmptyStateVisualProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  } = props;

  return (
    <div
      ref={forwardedRef}
      className={clsx([visualContainerCSS, visualSlotCss, consumerClassName])}
      style={consumerStyle}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
});

(Visual as typeof Visual & { displayName: string }).displayName =
  'EmptyState.Visual';
